import React from "react";
import {
  FaCode,
  FaDatabase,
  FaGlobe,
  FaShoppingCart,
  FaRocket,
  FaMobileAlt,
  FaServer,
  FaCloud,
  FaSearch,
} from "react-icons/fa";
import "../styles/ServicePage.css";

// Constants
const SOFTWARE_SERVICES = [
  {
    id: 1,
    title: "Custom Software Development",
    icon: FaCode,
    description:
      "Tailor-made software solutions designed specifically for your business needs.",
    features: [
      "Requirements Analysis & Planning",
      "Full-Stack Development",
      "Database Design & Optimization",
      "Testing & Quality Assurance",
      "Deployment & Maintenance",
      "Post-Launch Support",
    ],
    benefits: [
      "Scalable and maintainable code",
      "Future-proof architecture",
      "Enhanced productivity",
      "Competitive advantage",
      "Reduced operational costs",
    ],
  },
  {
    id: 2,
    title: "System Integration",
    icon: FaServer,
    description:
      "Seamlessly connect disparate systems to improve efficiency and data flow.",
    features: [
      "Legacy System Assessment",
      "Integration Planning",
      "API Development",
      "Data Migration",
      "System Testing",
      "Ongoing Support",
    ],
    benefits: [
      "Improved data accuracy",
      "Reduced manual work",
      "Better decision-making",
      "Increased efficiency",
      "Unified operations",
    ],
  },
  {
    id: 3,
    title: "API Development",
    icon: FaDatabase,
    description:
      "Robust and scalable APIs for seamless application communication.",
    features: [
      "RESTful API Design",
      "GraphQL APIs",
      "Authentication & Authorization",
      "Rate Limiting & Throttling",
      "Comprehensive Documentation",
      "Performance Optimization",
    ],
    benefits: [
      "Improved flexibility",
      "Third-party integrations",
      "Mobile app support",
      "Better scalability",
      "Enhanced security",
    ],
  },
  {
    id: 4,
    title: "Cloud Services",
    icon: FaCloud,
    description:
      "Cloud-based solutions for scalability, reliability, and cost-efficiency.",
    features: [
      "Cloud Architecture Design",
      "Migration Planning",
      "AWS/Azure/GCP Expertise",
      "DevOps Implementation",
      "Monitoring & Optimization",
      "Disaster Recovery",
    ],
    benefits: [
      "Pay-as-you-go pricing",
      "Automatic scaling",
      "Global accessibility",
      "Disaster recovery",
      "Reduced infrastructure costs",
    ],
  },
];

const WEB_SERVICES = [
  {
    id: 5,
    title: "Responsive Web Design",
    icon: FaGlobe,
    description:
      "Beautiful, fast, and mobile-friendly websites that work on all devices.",
    features: [
      "Mobile-First Approach",
      "Cross-Browser Compatibility",
      "Modern UI/UX Design",
      "Page Speed Optimization",
      "SEO-Friendly Architecture",
      "Accessibility Compliance",
    ],
    benefits: [
      "Better user experience",
      "Higher conversion rates",
      "Improved SEO rankings",
      "Reduced bounce rates",
      "Mobile traffic boost",
      "Professional appearance",
    ],
  },
  {
    id: 6,
    title: "E-Commerce Solutions",
    icon: FaShoppingCart,
    description:
      "Complete online store solutions with payment processing and inventory management.",
    features: [
      "Shopping Cart Development",
      "Payment Gateway Integration",
      "Inventory Management",
      "Order Management System",
      "Product Catalog",
      "Customer Analytics",
    ],
    benefits: [
      "Increased sales",
      "Customer data insights",
      "Automated operations",
      "Reduced overhead",
      "Global reach",
      "Easy scalability",
    ],
  },
  {
    id: 7,
    title: "Web Applications",
    icon: FaRocket,
    description:
      "Powerful web applications that feel like native desktop apps.",
    features: [
      "Progressive Web Apps (PWA)",
      "Real-Time Functionality",
      "Advanced Features",
      "User Authentication",
      "Data Management",
      "Cloud Integration",
    ],
    benefits: [
      "Offline functionality",
      "App-like experience",
      "Faster performance",
      "Lower deployment costs",
      "Easy updates",
      "Cross-platform compatibility",
    ],
  },
  {
    id: 8,
    title: "Mobile App Development",
    icon: FaMobileAlt,
    description:
      "Native and cross-platform mobile applications for iOS and Android.",
    features: [
      "iOS Development",
      "Android Development",
      "Cross-Platform (React Native)",
      "Push Notifications",
      "Offline Sync",
      "App Store Optimization",
    ],
    benefits: [
      "Wider audience reach",
      "Improved engagement",
      "Higher retention rates",
      "Monetization opportunities",
      "Brand loyalty",
      "Direct customer access",
    ],
  },
];

const WHY_CHOOSE_US = [
  {
    icon: FaCode,
    title: "Expert Developers",
    description: "15+ years of combined experience",
  },
  {
    icon: FaSearch,
    title: "Full-Stack Solutions",
    description: "From frontend to backend expertise",
  },
  {
    icon: FaRocket,
    title: "Modern Technologies",
    description: "Latest tools and best practices",
  },
];

const TECH_STACK = [
  {
    category: "Frontend",
    technologies: "React, Vue.js, Angular, HTML5, CSS3, JavaScript",
  },
  {
    category: "Backend",
    technologies: "Node.js, Python, Java, .NET, PHP, Ruby",
  },
  {
    category: "Database",
    technologies: "PostgreSQL, MongoDB, MySQL, Firebase",
  },
  {
    category: "Cloud & DevOps",
    technologies: "AWS, Azure, Google Cloud, Docker, Kubernetes",
  },
];

// Subcomponents
const ServiceCard = ({ service }) => {
  const IconComponent = service.icon;

  return (
    <div className="service-detail-card">
      <div className="service-detail-header">
        <IconComponent className="service-detail-icon" aria-hidden="true" />
        <div>
          <h3>{service.title}</h3>
          <p className="service-detail-desc">{service.description}</p>
        </div>
      </div>

      <div className="service-detail-content">
        <div className="detail-section">
          <h4>What We Offer</h4>
          <ul className="features-list">
            {service.features.map((feature, idx) => (
              <li key={idx}>✓ {feature}</li>
            ))}
          </ul>
        </div>

        <div className="detail-section">
          <h4>Key Benefits</h4>
          <ul className="benefits-list">
            {service.benefits.map((benefit, idx) => (
              <li key={idx}>★ {benefit}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

const WhyChooseSection = () => (
  <div className="service-intro">
    <h2>Why Choose Engineer K?</h2>
    <p>
      We specialize in creating robust, scalable, and user-friendly software
      solutions and web experiences. Whether you need a desktop application, web
      platform, or mobile app, our expert team delivers cutting-edge technology
      that drives growth and efficiency.
    </p>
    <div className="why-choose-grid">
      {WHY_CHOOSE_US.map((item, idx) => {
        const IconComponent = item.icon;
        return (
          <div key={idx} className="why-item">
            <IconComponent className="why-icon" aria-hidden="true" />
            <h3>{item.title}</h3>
            <p>{item.description}</p>
          </div>
        );
      })}
    </div>
  </div>
);

const TechStackSection = () => (
  <div className="tech-stack-section">
    <h2>Technologies We Use</h2>
    <div className="tech-stack-grid">
      {TECH_STACK.map((item, idx) => (
        <div key={idx} className="tech-item">
          <h4>{item.category}</h4>
          <p>{item.technologies}</p>
        </div>
      ))}
    </div>
  </div>
);

const CTASection = ({ onServiceClick }) => (
  <div className="cta-section">
    <h2>Ready to Build Your Next Project?</h2>
    <p>Contact us today for a free consultation</p>
    <button
      className="cta-button"
      onClick={() =>
        onServiceClick && onServiceClick("Software & Web Development")
      }
    >
      Contact Us Now
    </button>
  </div>
);

// Main Component
function SoftwareWebDevelopment({ onServiceClick }) {
  return (
    <div className="service-page">
      <div className="service-hero">
        <h1>Software & Web Development Solutions</h1>
        <p>
          Custom-built applications and websites tailored to your unique
          business requirements
        </p>
      </div>

      <div className="service-container">
        <WhyChooseSection />

        {/* Software Development Section */}
        <section className="service-section" aria-labelledby="software-heading">
          <h2 id="software-heading" className="section-title">
            Software Development Services
          </h2>
          <p className="section-description">
            Build powerful, enterprise-grade software solutions that scale with
            your business
          </p>
          {SOFTWARE_SERVICES.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </section>

        {/* Web Development Section */}
        <section className="service-section" aria-labelledby="web-heading">
          <h2 id="web-heading" className="section-title">
            Web Development Services
          </h2>
          <p className="section-description">
            Create stunning, high-performance web experiences that engage users
            and drive conversions
          </p>
          {WEB_SERVICES.map((service) => (
            <ServiceCard key={service.id} service={service} />
          ))}
        </section>

        <TechStackSection />
        <CTASection onServiceClick={onServiceClick} />
      </div>
    </div>
  );
}

export default SoftwareWebDevelopment;
